import { msg } from '@lingui/core/macro';

import { defineEnum } from '@/shared/patterns/define-enum';

// Rótulos, tons e ícones dos valores técnicos da operação (U19, U33). Arquivo leve de propósito: entra
// no `validateSearch` da rota (filtros), que vai para o JS inicial.

/**
 * Os 23 status com tom e ícone do protótipo (`credito.py`, `STATUS`). O rótulo segue o filtro do
 * legado (`creditNoteSelectFilterOptions`, discovery/02 §5.1), com "Liquidação" → "Pago".
 */
export const CREDIT_NOTE_STATUS = defineEnum(
  {
    Draft: { label: msg`Rascunho`, tone: 'neutral', icon: 'pencil' },
    Revision: { label: msg`Revisão`, tone: 'warn', icon: 'rotate-ccw' },
    Disapproved: { label: msg`Reprovado`, tone: 'err', icon: 'circle-x' },
    Error: { label: msg`Erro`, tone: 'err', icon: 'triangle-alert' },
    ComplianceApproval: { label: msg`Aprovação de Compliance`, tone: 'info', icon: 'shield-check' },
    CreditApproval: { label: msg`Aprovação de Crédito`, tone: 'info', icon: 'scale' },
    InstrumentApproval: { label: msg`Aprovação de Instrumento`, tone: 'info', icon: 'file-check' },
    MarginReserveApproval: { label: msg`Reserva de Margem`, tone: 'info', icon: 'lock' },
    VehicleInspection: { label: msg`Vistoria Veicular`, tone: 'warn', icon: 'car' },
    VehicleInspectionApproval: { label: msg`Aprovação da Vistoria Veicular`, tone: 'info', icon: 'car' },
    Signatures: { label: msg`Coleta de Assinaturas`, tone: 'warn', icon: 'pen-line' },
    SignaturesValidation: { label: msg`Validação de Assinaturas`, tone: 'info', icon: 'badge-check' },
    PartnerSignaturesValidation: { label: msg`Validação de assinatura pelo parceiro`, tone: 'info', icon: 'handshake' },
    Warranty: { label: msg`Garantia`, tone: 'warn', icon: 'shield' },
    ManualWarranty: { label: msg`Garantia Manual`, tone: 'warn', icon: 'shield' },
    WarrantyRevision: { label: msg`Revisão de Garantia`, tone: 'warn', icon: 'rotate-ccw' },
    WaitLiquidation: { label: msg`Aguardando Liberação para Liquidação`, tone: 'warn', icon: 'hourglass' },
    ManualLiquidation: { label: msg`Liquidação Manual`, tone: 'warn', icon: 'hand-coins' },
    Liquidation: { label: msg`Pago`, tone: 'ok', icon: 'circle-check' },
    PaymentRevision: { label: msg`Revisão de Pagamento`, tone: 'warn', icon: 'rotate-ccw' },
    OpenAccountRevision: { label: msg`Revisão de abertura de conta`, tone: 'warn', icon: 'rotate-ccw' },
    Finished: { label: msg`Encerrado`, tone: 'ok', icon: 'check-check' },
    Canceled: { label: msg`Cancelada`, tone: 'neutral', icon: 'ban' },
  },
  { order: 'label' },
);

/** `liquidationModeOptions` (services/creditNote/options). */
export const LIQUIDATION_MODE = defineEnum({
  Simple: { label: msg`Liquidação Simples` },
  LiquidationSchedule: { label: msg`Roteiro de Liquidação` },
});

/** `creditNoteOpLiquidationClosureStatus` (contexts/creditNote/creditNoteOptions.ts:209-217). */
export const CLOSURE_STATUS = defineEnum({
  Pending: { label: msg`Pendente` },
  SettleOperation: { label: msg`Operação quitada` },
  TransferChangeAccount: { label: msg`Transferência para conta troco` },
  TransitoryBankAccountClosed: { label: msg`Conta transitória encerrada` },
  Revision: { label: msg`Aguardando revisão` },
  Cancelled: { label: msg`Cancelado` },
  Finished: { label: msg`Finalizado` },
});

/** Sim/Não com os valores do fio (`selectOptionsYesOrNo`). */
export const YES_NO = defineEnum({ true: { label: msg`Sim` }, false: { label: msg`Não` } });

/**
 * Categorias de produto (`categoryNameOptions`, creditProductReadModel.ts:331-357). São nomes de
 * categoria da CreditAPI, iguais em qualquer idioma, e o valor vai ao fio como está.
 */
/* eslint-disable lingui/no-unlocalized-strings -- nomes de categoria da API, não texto de UI */
const CATEGORY_NAMES = [
  'ANTECIPAÇÃO DE RECEBÍVEIS',
  'CAR EQUITY',
  'CARTÃO BENEFÍCIO',
  'CCB',
  'CCB CLEAN',
  'CCB FINANCIAMENTO VE',
  'CCB PRICE',
  'CDC',
  'CDC FGTS',
  'COMPRA DE DÍVIDA',
  'CONSIGNADO INSS',
  'CONSIGNADO PRIVADO',
  'CONSIGNADO PRIVADO CDC',
  'CONSIGNADO PRIVADO LEILÃO',
  'CONSIGNADO PÚBLICO',
  'CRÉDITO PESSOAL',
  'DESCONTO',
  'FGTS',
  'FGTS COM SEGURO',
  'FINANCIAMENTO DE VEICULO',
  'FINANCIAMENTO ESTUDANTIL',
  'HOME EQUITY',
  'NOTA COMERCIAL',
  'PROCESSO TRABALHISTA',
  'VEÍCULO',
] as const;
export const PRODUCT_CATEGORY = defineEnum(
  Object.fromEntries(CATEGORY_NAMES.map((value) => [value, { label: value === 'FINANCIAMENTO DE VEICULO' ? 'FINANCIAMENTO DE VEÍCULO' : value }])) as Record<(typeof CATEGORY_NAMES)[number], { label: string }>,
);
/* eslint-enable lingui/no-unlocalized-strings */
